import { useState } from "react";
import { Box, ButtonBase, Drawer, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon } from "../../../icons";

/* ────────────── Shared UI pieces ────────────── */

interface DrawerShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

function DrawerShell({ open, onClose, title, subtitle, children }: DrawerShellProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 28px",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: "44px",
          height: "4px",
          borderRadius: "4px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />
      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 600,
          textAlign: "center",
          marginBottom: "4px",
          color: "text.primary",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: "text.secondary",
          textAlign: "center",
          marginBottom: "18px",
        }}
      >
        {subtitle}
      </Typography>
      <Box sx={{ display: "grid", gap: "10px" }}>{children}</Box>
    </Drawer>
  );
}

interface RowActiveProps {
  icon: string;
  label: string;
  subtitle: string;
  onClick: () => void;
}

function RowActive({ icon, label, subtitle, onClick }: RowActiveProps) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: "14px",
        width: "100%",
        padding: "14px",
        borderRadius: "18px",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.04)",
        transition: "background 0.15s",
        "&:active": {
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.08)",
        },
      })}
    >
      <Box
        sx={(theme) => ({
          width: 42,
          height: 42,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.06)",
          color: "text.primary",
          flexShrink: 0,
        })}
      >
        <Icon icon={icon} width={24} height={24} />
      </Box>
      <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "text.secondary", marginTop: "2px" }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ color: "text.secondary", opacity: 0.6, display: "flex", flexShrink: 0 }}>
        <ChevronRightIcon />
      </Box>
    </ButtonBase>
  );
}

interface RowDisabledProps {
  icon: string;
  label: string;
  subtitle: string;
  badge: string;
}

function RowDisabled({ icon, label, subtitle, badge }: RowDisabledProps) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: "14px",
        width: "100%",
        padding: "14px",
        borderRadius: "18px",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.03)"
            : "rgba(0, 0, 0, 0.025)",
        opacity: 0.75,
        cursor: "not-allowed",
        userSelect: "none",
      })}
    >
      <Box
        sx={(theme) => ({
          width: 42,
          height: 42,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.05)",
          color: "text.secondary",
          flexShrink: 0,
        })}
      >
        <Icon icon={icon} width={24} height={24} />
      </Box>
      <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "text.secondary", marginTop: "2px" }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          fontSize: "11px",
          fontWeight: 600,
          padding: "4px 10px",
          borderRadius: "999px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.06)",
          color: "text.secondary",
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        })}
      >
        {badge}
      </Box>
    </Box>
  );
}

/* ────────────── Main component ────────────── */

type Screen = "target" | "card_method";

interface MethodDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelectCrypto: () => void;
  title?: string;
  subtitle?: string;
  cryptoSubtitle?: string;
  fiatSubtitle?: string;
  /** Когда true — «Способ вывода» — одноуровневый, как раньше. */
  isWithdraw?: boolean;
  /**
   * Только для withdraw. Клик по «Перевести пользователю» — открывает
   * P2P-drawer. Если не передан, третья строка не рендерится (обратная
   * совместимость).
   */
  onSelectP2p?: () => void;
  p2pSubtitle?: string;
}

export function MethodDrawer({
  open,
  onClose,
  onSelectCrypto,
  title,
  subtitle,
  cryptoSubtitle,
  fiatSubtitle,
  isWithdraw,
  onSelectP2p,
  p2pSubtitle,
}: MethodDrawerProps) {
  const { t } = useTranslation(["home", "common"]);
  const [screen, setScreen] = useState<Screen>("target");

  const handleClose = () => {
    onClose();
    setTimeout(() => setScreen("target"), 300);
  };

  const handleSelectCrypto = () => {
    onSelectCrypto();
    setTimeout(() => setScreen("target"), 300);
  };

  /* ── Withdraw: одноуровневый (как было) ── */
  if (isWithdraw) {
    const resolvedTitle = title ?? t("home:flows.withdraw_method_title");
    const resolvedSubtitle = subtitle ?? t("home:flows.withdraw_method_subtitle");
    const resolvedCryptoSubtitle = cryptoSubtitle ?? t("home:flows.crypto_subtitle");
    const resolvedFiatSubtitle = fiatSubtitle ?? t("home:flows.fiat_subtitle_withdraw");
    const resolvedP2pSubtitle =
      p2pSubtitle ??
      t("home:flows.p2p_subtitle", {
        defaultValue: "Пользователю по e-mail или телефону",
      });

    return (
      <DrawerShell open={open} onClose={handleClose} title={resolvedTitle} subtitle={resolvedSubtitle}>
        <RowActive
          icon="solar:hand-money-bold-duotone"
          label={t("home:flows.crypto")}
          subtitle={resolvedCryptoSubtitle}
          onClick={handleSelectCrypto}
        />
        {onSelectP2p && (
          <RowActive
            icon="solar:users-group-two-rounded-bold-duotone"
            label={t("home:flows.p2p", {
              defaultValue: "Перевести пользователю",
            })}
            subtitle={resolvedP2pSubtitle}
            onClick={() => {
              onSelectP2p();
              setTimeout(() => setScreen("target"), 300);
            }}
          />
        )}
        <RowDisabled
          icon="solar:card-2-bold-duotone"
          label={t("home:flows.fiat")}
          subtitle={resolvedFiatSubtitle}
          badge={t("common:misc.coming_soon")}
        />
      </DrawerShell>
    );
  }

  /* ── Deposit: двухуровневый ── */
  if (screen === "card_method") {
    return (
      <DrawerShell
        open={open}
        onClose={handleClose}
        title={t("home:flows.topup_card_title")}
        subtitle={t("home:flows.topup_card_method_subtitle")}
      >
        <RowDisabled
          icon="solar:hand-money-bold-duotone"
          label={t("home:flows.via_crypto")}
          subtitle={t("home:flows.via_crypto_subtitle")}
          badge={t("common:misc.coming_soon")}
        />
        <RowDisabled
          icon="solar:card-2-bold-duotone"
          label={t("home:flows.via_sbp")}
          subtitle={t("home:flows.via_sbp_subtitle")}
          badge={t("common:misc.coming_soon")}
        />
      </DrawerShell>
    );
  }

  /* screen === "target" */
  const resolvedTitle = title ?? t("home:flows.deposit_method_title");
  const resolvedSubtitle = subtitle ?? t("home:flows.deposit_method_subtitle");

  return (
    <DrawerShell open={open} onClose={handleClose} title={resolvedTitle} subtitle={resolvedSubtitle}>
      <RowActive
        icon="solar:card-2-bold-duotone"
        label={t("home:flows.topup_card")}
        subtitle={t("home:flows.topup_card_subtitle")}
        onClick={() => setScreen("card_method")}
      />
      <RowActive
        icon="solar:wallet-bold-duotone"
        label={t("home:flows.topup_wallet")}
        subtitle={t("home:flows.topup_wallet_subtitle")}
        onClick={handleSelectCrypto}
      />
    </DrawerShell>
  );
}

export const DepositMethodDrawer = MethodDrawer;
